import { useQueryClient } from "@tanstack/react-query";
import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { API_ENDPOINT, API_REQUEST_TIMEOUT, APP_LOGIN_ORIGIN, AUTH_TOKEN_KEY, SELECTED_WORKSPACE_KEY, USER_DETAILS_KEY } from '../constants';
import StorageService from "../services/StorageService";
import { TriggerToast } from "../components/globals/Toaster";
import { trackApiAction } from "../services/AppAnalytics";
import { appRoutes } from "../routes/appRoutes";
import { hasBrowserEventListeners } from "../utils/browserEvent";
import { isExtensionRuntime } from "../utils/appSurface";
import { getWebLoginUrl, isWebSessionPayload } from "../components/auth/webSessionPolicy";

type AuthToken = string | boolean | null

type AxiosContextType = {
  axiosApiInstance: AxiosInstance
  setToken: (token: AuthToken) => void
}

type AxiosProviderProps = {
  children: React.ReactNode
}

const axiosApiInstance: AxiosInstance = axios.create({
  baseURL: API_ENDPOINT,
  timeout: API_REQUEST_TIMEOUT,
  headers: { 'accept': 'application/json', 'Content-Type': 'application/json' }
})

const AxiosContext = createContext<AxiosContextType | null>(null)

const getRequestErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return "Request failed. Please try again.";
  }

  if (error.code === "ECONNABORTED") {
    return "Request timed out. Please try again.";
  }

  if (!error.response) {
    return "Network request failed. Please check your connection.";
  }

  switch (error.response.status) {
    case 400:
      return "The request could not be completed. Please check your input and try again.";
    case 403:
      return "You do not have access to complete this action.";
    case 404:
      return "We could not find the requested data.";
    case 409:
      return "This action conflicts with the latest data.";
    case 422:
      return "Some information is invalid. Please review and try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    default:
      if (error.response.status >= 500) {
        return "Something went wrong on our side. Please try again.";
      }
      return "Request failed. Please try again.";
  }
};

export const AxiosProvider: React.FC<AxiosProviderProps> = ({ ...props }) => {

  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [token, setTokenState] = useState<AuthToken>(null)
  const [isReady, setIsReady] = useState(false)
  const tokenRef = useRef(token)
  const locationPathRef = useRef(location.pathname)
  const navigateRef = useRef(navigate)
  const extensionRuntime = isExtensionRuntime()

  const redirectWebToLogin = useCallback(() => {
    window.location.replace(getWebLoginUrl(APP_LOGIN_ORIGIN, window.location))
  }, [])

  const setToken = useCallback((nextToken: AuthToken) => {
    tokenRef.current = nextToken
    setTokenState(nextToken)
  }, [])

  locationPathRef.current = location.pathname
  navigateRef.current = navigate

  useEffect(() => {
    const runtimeMessages = typeof chrome !== 'undefined'
      ? chrome.runtime?.onMessage
      : undefined
    const storageChanges = typeof chrome !== 'undefined'
      ? chrome.storage?.onChanged
      : undefined

    const webStorageListener = async (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage || event.key !== AUTH_TOKEN_KEY) return

      if (event.newValue) {
        tokenRef.current = event.newValue
        setToken(event.newValue)
        await queryClient.invalidateQueries()
        return
      }

      if (event.oldValue) {
        tokenRef.current = false
        setToken(false)
        await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY)
        await StorageService.removeStorageItem(USER_DETAILS_KEY)
        queryClient.clear()
        if (extensionRuntime) {
          navigateRef.current(appRoutes.login, { replace: true })
        } else {
          redirectWebToLogin()
        }
      }
    }

    window.addEventListener('storage', webStorageListener)

    if (!hasBrowserEventListeners(runtimeMessages) || !hasBrowserEventListeners(storageChanges)) {
      return () => window.removeEventListener('storage', webStorageListener)
    }

    const refreshListener = async (message: { action: string }) => {
      if (message.action === 'refresh') {
        await queryClient.invalidateQueries();
        await queryClient.resetQueries();
        setTimeout(() => navigate(appRoutes.experiments.root), 500);
      }
    };

    const tokenListener = async (message: { action: string, token: string | boolean | null }) => {
      if (message.action === 'prismancy_token') {
        if (message.token === false || !message.token) {
          // Logout - clear auth state
          console.log('[AxiosContext] Logout detected from message')
          tokenRef.current = false
          setToken(false)
          await StorageService.removeStorageItem(AUTH_TOKEN_KEY)
          await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY)
          queryClient.clear()
          navigate('/', { replace: true })
        } else {
          // Login - update token and refresh queries
          console.log('[AxiosContext] Login detected from message')
          tokenRef.current = message.token
          setToken(message.token)
          await queryClient.invalidateQueries()
        }
      }
    };

    // Listen for chrome.storage changes as backup
    const storageListener = async (changes: { [key: string]: chrome.storage.StorageChange }, areaName: string) => {
      if (areaName !== 'local') return

      // Handle token changes
      if (changes[AUTH_TOKEN_KEY]) {
        const newToken = changes[AUTH_TOKEN_KEY].newValue
        const oldToken = changes[AUTH_TOKEN_KEY].oldValue

        if (newToken && newToken !== oldToken) {
          // Token added/changed - login
          console.log('[AxiosContext] Login detected from storage change')
          tokenRef.current = newToken
          setToken(newToken)
          await queryClient.invalidateQueries()
        } else if (!newToken && oldToken) {
          // Token removed - logout
          console.log('[AxiosContext] Logout detected from storage change')
          tokenRef.current = false
          setToken(false)
          await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY)
          queryClient.clear()
          navigate('/', { replace: true })
        }
      }
    };

    runtimeMessages.addListener(refreshListener);
    runtimeMessages.addListener(tokenListener);
    storageChanges.addListener(storageListener);

    return () => {
      window.removeEventListener('storage', webStorageListener)
      runtimeMessages.removeListener(refreshListener);
      runtimeMessages.removeListener(tokenListener);
      storageChanges.removeListener(storageListener);
    };
  }, [extensionRuntime, queryClient, redirectWebToLogin, setToken]);

  useEffect(() => {
    const getToken = async () => {
      const authToken = await StorageService.getStorageItem<string>(AUTH_TOKEN_KEY)
      if (authToken) {
        tokenRef.current = authToken
        setToken(authToken)
      } else if (extensionRuntime || locationPathRef.current === "/auth/handoff") {
        tokenRef.current = false
        setToken(false)
      } else {
        try {
          const response = await fetch(`${APP_LOGIN_ORIGIN}/api/session`, {
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "application/json" },
          })
          if (!response.ok) throw new Error("Session unavailable")
          const session: unknown = await response.json()
          if (!isWebSessionPayload(session)) throw new Error("Invalid session")

          const workspaceId = session.active_workspace?.[0]?.id
          if (workspaceId) {
            await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, workspaceId)
          } else {
            await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY)
          }
          await StorageService.setStorageItem(AUTH_TOKEN_KEY, session.access_token)
          tokenRef.current = session.access_token
          setToken(session.access_token)
        } catch {
          tokenRef.current = false
          setToken(false)
          redirectWebToLogin()
        }
      }
    }
    void getToken()
  }, [extensionRuntime, redirectWebToLogin, setToken])

  useEffect(() => {

    const requestInterceptor = axiosApiInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const currentToken = tokenRef.current
      if (typeof currentToken === "string" && currentToken) {
        config.headers['Authorization'] = `Bearer ${currentToken}`
      } else {
        delete config.headers['Authorization']
      }
      return config
    }, (error) => {
      TriggerToast(getRequestErrorMessage(error), undefined, 'error');
      return Promise.reject(error)
    })

    const responseInterceptor = axiosApiInstance.interceptors.response.use((response) => {
      trackApiAction(response.config, response.status, "success")
      return response
    }, async (error) => {
      if (axios.isAxiosError(error)) {
        trackApiAction(error.config, error.response?.status || 0, "failure")
      }

      if (axios.isAxiosError(error)) {
        if (!error.response || error.response.status !== 401) {
          TriggerToast(getRequestErrorMessage(error), undefined, 'error');
        }
      }

      if (error?.response) {
        switch (error.response.status) {
          case 401:
            if (tokenRef.current) {
              tokenRef.current = false
              setToken(false)
              await StorageService.removeStorageItem(AUTH_TOKEN_KEY);
              await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY);
              queryClient.clear()
              if (!locationPathRef.current.startsWith('/login') && extensionRuntime) {
                navigateRef.current('/', { replace: true })
              } else if (!extensionRuntime) {
                redirectWebToLogin()
              }
            }
            break
        }
      }

      return Promise.reject(error)
    })

    setIsReady(true)

    return () => {
      axiosApiInstance.interceptors.request.eject(requestInterceptor)
      axiosApiInstance.interceptors.response.eject(responseInterceptor)
    }
  }, [extensionRuntime, queryClient, redirectWebToLogin, setToken])

  // if either the interceptors are not yet ready or tokens is not yet set
  if (isReady === false || token === null) {
    return null
  }

  return (
    <AxiosContext.Provider value={{ axiosApiInstance, setToken }} {...props} />
  )
}

export const useAxios = () => {
  const instance = React.useContext(AxiosContext)
  if (!instance) {
    throw new Error("useAxios must be used within a AxiosProvider");
  }
  return instance
}
